// The offline store for one account: the projects list as `/api/projects` returns it (wrapped keys and sealed
// summaries, never opened keys), each project's sealed entries and cursor, and a little per-project bookkeeping
// (word counts the core computed, when it was last opened here). `<project>` is the project's address
// (client/net/address.ts): its id for the account's own, `<owner>/<id>` for one shared with it (W9a), so an owner's
// project and a member's own project with the same id never share a key. Keys:
//   last-account                     the account last signed in here (so an offline reload knows whose cache it is)
//   projects:<account>               { projects: WebProject[], fetchedAt }
//   shares:<account>                 { shares: Share[], fetchedAt }: projects shared with the account, as `/api/shares`
//                                    lists them (wrapped keys and sealed summaries only); a share the relay no longer
//                                    lists stays, marked removed, so its copy here can still be exported
//   rotation:<account>:<project>     a key rotation this browser began and hasn't finished (`PendingRotation`: the new
//                                    key wrapped under the AVK, and the documents re-uploaded so far)
//   cursor:<account>:<project>       the last seq stored
//   info:<account>:<project>         ProjectInfo
//   outbox:<account>:<project>:<key> this device's batches waiting for the relay (sealed, as `OutboxItem`s)
//   device:<account>                 this browser's device id (word logs are kept per device), for offline starts
//   archive:<account>:<project>:<doc>:<epoch>:<lineage>
//                                    an older history's copy of a document, kept (sealed) after a newer epoch was
//                                    adopted, to absorb what still arrives in it (`StoredArchive`)
import { isShared } from '@writing-suite/client/net/address.ts';
import { outboxKey, type OutboxItem, type OutboxStore } from '@writing-suite/client/net/outbox.ts';
import type { Share } from '@writing-suite/client/net/shares.ts';
import type { WebProject } from '@writing-suite/client/net/vault.ts';
import type { Backend, CachedEntry } from './backend.ts';

export type { CachedEntry } from './backend.ts';

export interface ProjectList {
  projects: WebProject[];
  fetchedAt: number;
}

export interface ShareList {
  shares: Share[];
  fetchedAt: number;
}

/** A rotation under way (W9a, PROTOCOL.md §10.7): resumed at the project's next open until every document is uploaded. */
export interface PendingRotation {
  /** The new key, wrapped under the AVK as the project's `wrappedKey` is. */
  wrappedKey: string;
  fingerprint: string;
  /** The key before (wrapped likewise), so what the old key sealed still opens here. */
  previousWrappedKey: string;
  /** The rotation was recorded by the relay (`POST …/rotate` answered). */
  posted: boolean;
  /** Documents re-uploaded as snapshots under the new key. */
  uploaded: string[];
  startedAt: number;
}

/** What the Library shows before a project's documents load: filled in from the core each time it opens. */
export interface ProjectInfo {
  title?: string;
  type?: string;
  series?: string | null;
  /** The core's `manuscriptWords`, as of `wordsAt` (a cursor). */
  words?: number;
  wordsAt?: number;
  lastOpened?: number;
}

/** An archived older history of a document (sealed like entries), with what of it was synced and what carried over. */
export interface StoredArchive {
  doc: string;
  epoch: number;
  lineage: string;
  synced: string[];
  rebased: string[];
  sealed: Uint8Array;
}

/** A project's documents compacted into one entry per document when it has more than this many cached entries. */
export const COMPACT_AFTER = 64;

export class OfflineStore {
  constructor(
    readonly backend: Backend,
    readonly accountId: string,
  ) {}

  get persistent(): boolean {
    return this.backend.persistent;
  }

  static lastAccount(backend: Backend): Promise<string | undefined> {
    return backend.getMeta<string>('last-account');
  }

  static setLastAccount(backend: Backend, accountId: string): Promise<boolean> {
    return backend.putMeta('last-account', accountId);
  }

  private key(kind: string, project?: string) {
    return project === undefined ? `${kind}:${this.accountId}` : `${kind}:${this.accountId}:${project}`;
  }

  async projects(): Promise<ProjectList | null> {
    return (await this.backend.getMeta<ProjectList>(this.key('projects'))) ?? null;
  }

  async putProjects(projects: WebProject[], now = Date.now()): Promise<void> {
    await this.backend.putMeta(this.key('projects'), { projects, fetchedAt: now } satisfies ProjectList);
    // Projects taken off the web lose their cached documents here too (the account's own: a shared project's copy is
    // the shares list's to keep or forget).
    const keep = new Set(projects.map((p) => p.id));
    for (const k of await this.backend.metaKeys(`cursor:${this.accountId}:`)) {
      const id = k.slice(`cursor:${this.accountId}:`.length);
      if (!isShared(id) && !keep.has(id)) await this.forget(id);
    }
  }

  async shares(): Promise<ShareList | null> {
    return (await this.backend.getMeta<ShareList>(this.key('shares'))) ?? null;
  }

  /** Keeps the shares list (the caller merges in the removed ones it keeps). */
  async putShares(shares: Share[], now = Date.now()): Promise<void> {
    await this.backend.putMeta(this.key('shares'), { shares, fetchedAt: now } satisfies ShareList);
  }

  async rotation(project: string): Promise<PendingRotation | null> {
    return (await this.backend.getMeta<PendingRotation>(this.key('rotation', project))) ?? null;
  }

  async putRotation(project: string, r: PendingRotation | null): Promise<void> {
    if (r) await this.backend.putMeta(this.key('rotation', project), r);
    else await this.backend.deleteMeta(this.key('rotation', project));
  }

  async cursor(project: string): Promise<number> {
    return (await this.backend.getMeta<number>(this.key('cursor', project))) ?? 0;
  }

  entries(project: string): Promise<CachedEntry[]> {
    return this.backend.entries(this.accountId, project);
  }

  /** Stores new entries (and the cursor they bring the project to). */
  append(project: string, entries: CachedEntry[], cursor: number): Promise<boolean> {
    return this.backend.addEntries(this.accountId, project, entries, this.key('cursor', project), cursor);
  }

  /** Replaces everything kept for a project. */
  replace(project: string, entries: CachedEntry[], cursor: number): Promise<boolean> {
    return this.backend.replaceEntries(this.accountId, project, entries, this.key('cursor', project), cursor);
  }

  async info(project: string): Promise<ProjectInfo> {
    return (await this.backend.getMeta<ProjectInfo>(this.key('info', project))) ?? {};
  }

  async putInfo(project: string, change: Partial<ProjectInfo>): Promise<ProjectInfo> {
    const next = { ...(await this.info(project)), ...change };
    await this.backend.putMeta(this.key('info', project), next);
    return next;
  }

  /** This device's batches for a project, waiting for the relay: sealed, kept until it acknowledges each. */
  outbox(project: string): OutboxStore {
    const prefix = `${this.key('outbox', project)}:`;
    return {
      load: async () => {
        const items: OutboxItem[] = [];
        for (const k of await this.backend.metaKeys(prefix)) {
          const item = await this.backend.getMeta<OutboxItem>(k);
          if (item) items.push(item);
        }
        return items;
      },
      put: (item) => this.backend.putMeta(prefix + outboxKey(item), item),
      remove: (item) => this.backend.deleteMeta(prefix + outboxKey(item)),
    };
  }

  /** Older histories' copies of this project's documents (after epoch adoptions). */
  async archives(project: string): Promise<StoredArchive[]> {
    const out: StoredArchive[] = [];
    for (const k of await this.backend.metaKeys(`${this.key('archive', project)}:`)) {
      const a = await this.backend.getMeta<StoredArchive>(k);
      if (a) out.push(a);
    }
    return out;
  }

  putArchive(project: string, archive: StoredArchive): Promise<boolean> {
    return this.backend.putMeta(`${this.key('archive', project)}:${archive.doc}:${archive.epoch}:${archive.lineage}`, archive);
  }

  async device(): Promise<string | undefined> {
    return this.backend.getMeta<string>(this.key('device'));
  }

  async setDevice(id: string): Promise<void> {
    if (id && (await this.device()) !== id) await this.backend.putMeta(this.key('device'), id);
  }

  async forget(project: string): Promise<void> {
    await this.backend.clearEntries(this.accountId, project);
    await this.backend.deleteMeta(this.key('cursor', project));
    await this.backend.deleteMeta(this.key('info', project));
    for (const k of await this.backend.metaKeys(`${this.key('outbox', project)}:`)) await this.backend.deleteMeta(k);
    for (const k of await this.backend.metaKeys(`${this.key('archive', project)}:`)) await this.backend.deleteMeta(k);
    await this.backend.deleteMeta(this.key('rotation', project));
  }

  /**
   * Every cached entry of a project opened with one key and sealed with another (after a rotation), so the copy here
   * reads with the project's key now. Entries no key opens are left as they are.
   */
  async reseal(project: string, open: (sealed: Uint8Array) => Promise<Uint8Array | null>, seal: (bytes: Uint8Array) => Promise<Uint8Array>): Promise<number> {
    const entries = await this.entries(project);
    let n = 0;
    const out: CachedEntry[] = [];
    for (const e of entries) {
      const plain = await open(e.bytes);
      if (plain) {
        out.push({ ...e, bytes: await seal(plain) });
        n++;
      } else out.push(e);
    }
    if (n) await this.replace(project, out, await this.cursor(project));
    return n;
  }

  /** Batches waiting for the relay across every project (signing out would lose them). */
  async unsent(): Promise<number> {
    return (await this.backend.metaKeys(`outbox:${this.accountId}:`)).length;
  }

  /** Signing out, or another account signing in: nothing of this account's stays. */
  async wipe(): Promise<void> {
    await this.backend.clearEntries(this.accountId);
    for (const prefix of ['cursor', 'info', 'outbox', 'archive', 'rotation']) for (const k of await this.backend.metaKeys(`${prefix}:${this.accountId}:`)) await this.backend.deleteMeta(k);
    await this.backend.deleteMeta(this.key('projects'));
    await this.backend.deleteMeta(this.key('shares'));
    await this.backend.deleteMeta(this.key('device'));
  }
}
